import type { ThreadMessage } from "@assistant-ui/react";
import type {
  ChatAckPayload,
  ChatSendPayload,
  QualityProfile,
  SessionResumePayload,
  SocketAuthPayload,
  SocketTelemetryPayload,
} from "./types";

export type SocketSessionIdentity = {
  guestId: string | null;
  threadId: string | null;
  memoryId: string | null;
  principalId: string | null;
  identityType: string | null;
  /** Ping Identity user-subject JWT for authenticated sessions. */
  userToken: string | null;
};

export type SocketInitialSessionIdentity = Partial<
  Pick<SocketSessionIdentity, "guestId" | "threadId" | "memoryId" | "userToken">
>;

export function createSessionIdentity(initialIdentity: SocketInitialSessionIdentity = {}): SocketSessionIdentity {
  return {
    guestId: initialIdentity.guestId ?? null,
    threadId: initialIdentity.threadId ?? null,
    memoryId: initialIdentity.memoryId ?? null,
    principalId: null,
    identityType: null,
    userToken: initialIdentity.userToken ?? null,
  };
}

export function createEmptySessionIdentity(): SocketSessionIdentity {
  return createSessionIdentity();
}

export function applySessionResume(
  current: SocketSessionIdentity,
  payload: SessionResumePayload,
): SocketSessionIdentity {
  return {
    guestId: payload.guest_id ?? current.guestId,
    threadId: payload.thread_id ?? current.threadId,
    memoryId: payload.memory_id ?? current.memoryId,
    principalId: payload.principal_id ?? current.principalId,
    identityType: payload.identity_type ?? current.identityType,
    // userToken is not part of the server resume payload — preserve the client-side value.
    userToken: current.userToken,
  };
}

export function applyChatAck(current: SocketSessionIdentity, payload: ChatAckPayload): SocketSessionIdentity {
  return {
    ...current,
    guestId: payload.guest_id ?? current.guestId,
    threadId: payload.thread_id ?? current.threadId,
    memoryId: payload.memory_id ?? current.memoryId,
  };
}

export function buildSocketAuth(identity: SocketSessionIdentity): SocketAuthPayload {
  return {
    ...(identity.guestId ? { guest_id: identity.guestId } : {}),
    ...(identity.userToken ? { user_token: identity.userToken } : {}),
  };
}

export function createClientMessageId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && "randomUUID" in cryptoApi) {
    return `msg-${cryptoApi.randomUUID()}`;
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function extractLastUserText(messages: readonly ThreadMessage[]): string {
  const lastUser = [...messages].reverse().find((message) => message.role === "user");
  if (!lastUser) return "";

  return lastUser.content
    .filter((part): part is { type: "text"; text: string } => part.type === "text")
    .map((part) => part.text)
    .join("");
}

export function extractLastUserClientMessageId(messages: readonly ThreadMessage[]): string | null {
  const lastUser = [...messages].reverse().find((message) => message.role === "user");
  if (!lastUser) return null;

  const custom = lastUser.metadata.custom as
    | {
        clientMessageId?: unknown;
        liveAssistance?: { clientMessageId?: unknown };
      }
    | undefined;
  const clientMessageId = custom?.clientMessageId ?? custom?.liveAssistance?.clientMessageId;
  return typeof clientMessageId === "string" && clientMessageId.trim() ? clientMessageId : null;
}

export function buildChatSendPayload({
  message,
  clientMessageId = createClientMessageId(),
  qualityProfile,
  modelId,
  telemetry,
}: {
  message: string;
  clientMessageId?: string;
  qualityProfile?: QualityProfile;
  modelId?: string;
  telemetry?: SocketTelemetryPayload;
}): ChatSendPayload {
  return {
    message,
    client_message_id: clientMessageId,
    ...(qualityProfile ? { quality_profile: qualityProfile } : {}),
    ...(modelId ? { model_id: modelId } : {}),
    ...(telemetry ? { telemetry } : {}),
  };
}
